import { useState, useEffect } from "react";

/**
 * Computes and reactively updates the remaining time until a cooldown expires.
 * Re-renders every second while the cooldown is active.
 *
 * @param cancelableAt  The Date when cancellation becomes available (null = no cooldown)
 * @returns  Object with isCoolingDown flag and a human-readable remainingLabel string
 */
export const useCooldownTimer = (cancelableAt: Date | null) => {
  const [now, setNow] = useState(() => new Date());

  const isCoolingDown = cancelableAt !== null && now < cancelableAt;

  // Tick every second while cooling down
  useEffect(() => {
    if (!isCoolingDown) return;
    const id = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(id);
  }, [isCoolingDown]);

  let remainingLabel = "";
  if (isCoolingDown && cancelableAt) {
    const diffMs = cancelableAt.getTime() - now.getTime();
    const totalSeconds = Math.max(0, Math.floor(diffMs / 1000));
    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;

    if (hours > 0) {
      remainingLabel = `${hours} sa ${minutes} dk`;
    } else if (minutes > 0) {
      remainingLabel = `${minutes} dk ${seconds} sn`;
    } else {
      remainingLabel = `${seconds} sn`;
    }
  }

  return { isCoolingDown, remainingLabel };
};